import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Spinner,
  Tag,
  Text,
} from '@nimbus-ds/components';
import { LinkOffIcon } from '@nimbus-ds/icons';
import { PartnersListItemResponseV2Dto } from '@tiendanube/common';
import { Stack } from 'commons/components';
import { useStoreStatus } from 'domains/Auth/hooks';
import { useUpdatePartnerAdminAccessMutation } from 'domains/Configurations/Users/usersApi';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';
import ShowMoreRoles from '../../../UsersListDesktop/components/ShowMoreRoles';

interface PartnersListMobileRowProps {
  partner?: PartnersListItemResponseV2Dto;
  storeRoles: Record<string, string>;
  hasPartnerAdminAccess?: boolean;
  onUnlinkPartner: (partner: PartnersListItemResponseV2Dto) => void;
}

const VIEWABLE_TAG_AMOUNT = 7;

function PartnersListMobileRow({
  partner,
  storeRoles,
  hasPartnerAdminAccess,
  onUnlinkPartner,
}: Readonly<PartnersListMobileRowProps>) {
  const t = useTranslationConfigurations('usersListPage.table');
  const [shownRoles, setShownRoles] = useState(7);

  const { storeStatus } = useStoreStatus();
  const showMoreRoles = () => setShownRoles((prev) => prev + 10);
  const showLessRoles = () => setShownRoles((prev) => prev - 10);

  const filteredPermissions = useMemo(
    () =>
      partner?.roles?.filter?.((permission) => {
        const label = storeRoles?.[permission];
        return typeof label === 'string' && label.trim().length > 0;
      }),
    [partner?.roles, storeRoles],
  );

  const [updatePartnerAdminAccess, { isLoading: loadingUpdateAdminAccess }] =
    useUpdatePartnerAdminAccessMutation();

  const showMoreRolesLink =
    (filteredPermissions ?? []).length > VIEWABLE_TAG_AMOUNT;
  const hasMoreRoles = (filteredPermissions ?? []).length > shownRoles;

  const handleAcceptOrRefuse = (
    permissionId: number,
    partnerId: number,
    status: boolean,
  ) => {
    updatePartnerAdminAccess({
      payload: {
        data: { permissionId, partnerId, status },
      },
    });
  };

  return (
    <Box width="100" padding="3">
      <Stack align="flex-start" gap="none">
        <Stack.Item fill>
          <Stack column align="flex-start" spacing="tight">
            <Text color="neutral-textHigh">
              {partner?.partnerName || t('withoutName')}
            </Text>
            <Text color="neutral-textHigh">{partner?.partnerEmail}</Text>
            <Box display="flex" gap="3">
              <Stack column align="flex-start" spacing="tight">
                <Stack spacing="tight" wrap>
                  {(filteredPermissions ?? [])
                    .slice(0, shownRoles)
                    .map((permission) => (
                      <Tag key={`partner-${partner?.partnerId}-${permission}`}>
                        <Text fontSize="caption" lineClamp={1}>
                          {storeRoles[permission]}
                        </Text>
                      </Tag>
                    ))}
                </Stack>
                {!!showMoreRolesLink && (
                  <ShowMoreRoles
                    hasMoreRoles={hasMoreRoles ?? false}
                    showMoreRoles={showMoreRoles}
                    showLessRoles={showLessRoles}
                  />
                )}
              </Stack>
            </Box>
          </Stack>
        </Stack.Item>
        {hasPartnerAdminAccess && partner?.status === 'pending' ? (
          <Box
            display="flex"
            gap="2"
            alignItems="center"
            flexDirection="column"
          >
            <Button
              appearance="neutral"
              onClick={() =>
                handleAcceptOrRefuse(partner?.id, partner?.partnerId, false)
              }
              disabled={loadingUpdateAdminAccess}
            >
              {t('refuse')}
              {loadingUpdateAdminAccess && (
                <Spinner color="currentColor" size="small" />
              )}
            </Button>
            <Button
              appearance="primary"
              onClick={() =>
                handleAcceptOrRefuse(partner?.id, partner?.partnerId, true)
              }
              disabled={loadingUpdateAdminAccess}
            >
              {t('accept')}
              {loadingUpdateAdminAccess && (
                <Spinner color="currentColor" size="small" />
              )}
            </Button>
          </Box>
        ) : partner?.status === 'granted' ? (
          <Box minWidth="2rem">
            <IconButton
              size="2rem"
              source={<LinkOffIcon />}
              onClick={() => onUnlinkPartner(partner)}
              disabled={storeStatus === 'development'}
            />
          </Box>
        ) : (
          <Box>-</Box>
        )}
      </Stack>
    </Box>
  );
}

export default PartnersListMobileRow;
